import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";

import { ErrorType } from "@/types";
import { InvoiceStatus, InvoiceType, TaxType } from "@/types/enums";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronDown } from "lucide-react";
import { useUpdateSystemSettingsMutation } from "@/services/systemConfigApi";
import { SystemSettings } from "@/types/system-setting/system-config";
import { Switch } from "@/components/ui/switch";
interface SRBSettingsProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: any;
  editModeData: any | undefined;
}
const SRBSettings = ({
  isOpen,
  setOpen,
  action,
  setAction,
  editModeData,
  refetch,
}: SRBSettingsProps) => {

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const userInfo = useSelector((state: RootState) => state.auth.userInfo?.user);
  const { toast } = useToast();
  const [taxType, setTaxType] = useState("exclusive");
  const [updateSetting, { isLoading, error, status }] = useUpdateSystemSettingsMutation();

  const initialValues = {
    group_name: "srb",
    api_url: "",
    api_key: "",
    pos_id: "",
    ntn: "",
    username: "",
    org_name: "",
  };

  const form = useForm<SystemSettings>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  useEffect(() => {
    if (action == "edit") {
      const payload = { ...editModeData, group_name: "srb" };
      reset(payload as SystemSettings);
    } else {
      reset(initialValues);
    }
  }, [action]);

  const onSubmit = async (formData: SystemSettings) => {
    const { group_name, ...payload } = formData
    try {
      const resp = await updateSetting({ srb: payload }).unwrap();
      if (!error && (status === "fulfilled" || status == 'uninitialized')) {
        toast({
          variant: "success",
          title: "Invoice updated Successfully",
        });
        refetch();
        handleClose();
        return resp;
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
        return false;
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
        return false;
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    reset(
      {},
      {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      }
    );

    setOpen(false);
  };

  console.log({ watcher, errors, action });
  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1100px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-6 sticky top-0 z-40 pt-4 bg-white">
              <SheetTitle>
                <div className="flex justify-between flex-col sm:flex-row gap-3 sm:gap-5 items-start bg-white">
                  <div>
                    <p className="font-semibold">System Configuration</p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        System Configuration
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {action == "add" ? "Create" : "Edit"} SRB Settings
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-end sm:justify-center space-x-3 sm:space-x-[20px] w-full sm:w-auto">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      type="submit"
                      className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                    >
                      {!isSubmitting &&
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      }
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full     " />

            <SheetDescription className=" h-full  p-6  ">
              <Card className="w-full ">
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    SRB Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-4">

                    {/* API Key */}
                    <div className="space-y-2">
                      <Label className="text-sm text-zinc-500">API Key</Label>
                      <Input
                        id="apiKey"
                        placeholder="Enter API Key"
                        className="pr-8 placeholder:text-zinc-400"
                        value={watcher.api_key}
                        {...register("api_key", {
                          required: "Required",
                          minLength: {
                            value: 1,
                            message: "Required",
                          },
                          setValueAs: (value) => value.trim(),
                        })}
                      />
                      {errors.api_key && (
                        <p className="text-red-500 text-sm mt-[5px]">
                          {errors.api_key.message}
                        </p>
                      )}
                    </div>
                    {/* API URL */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="invoicePrefix"
                        className="text-sm text-zinc-500"
                      >
                        API URL
                      </Label>
                      <div className="relative">
                        <Input
                          id="apiUrl"
                          placeholder="Enter API URL"
                          className="pr-8 placeholder:text-zinc-400"
                          value={watcher.api_url}
                          {...register("api_url", {
                            required: "Required",
                            minLength: {
                              value: 1,
                              message: "Required",
                            },
                            setValueAs: (value) => value.trim(),
                          })}
                        />
                        {errors.api_url && (
                          <p className="text-red-500 text-sm mt-[5px]">
                            {errors.api_url.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* POS ID Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="posId"
                        className="text-sm text-zinc-500"
                      >
                        POS ID
                      </Label>
                      <div className="relative">
                        <Input
                          id="posId"
                          defaultValue="0981"
                          className="pr-8 placeholder:text-zinc-400"
                          placeholder="Enter POS ID"
                          value={watcher.pos_id}
                          {...register("pos_id", {
                            required: "Required",
                            minLength: {
                              value: 1,
                              message: "Required",
                            },
                            setValueAs: (value) => value.trim(),
                          })}

                        />
                        {errors.pos_id && (
                          <p className="text-red-500 text-sm mt-[5px]">
                            {errors.pos_id.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* NTN */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="ntn"
                        className="text-sm text-zinc-500"
                      >
                        NTN
                      </Label>
                      <div className="relative">
                        <Input
                          id="ntn"
                          defaultValue="0981"
                          className="pr-8 placeholder:text-zinc-400"
                          placeholder="Enter NTN"
                          value={watcher.ntn}
                          {...register("ntn", {
                            required: "Required",
                            minLength: {
                              value: 1,
                              message: "Required",
                            },
                            setValueAs: (value) => value.trim(),
                          })}
                        />
                        {errors.ntn && (
                          <p className="text-red-500 text-sm mt-[5px]">
                            {errors.ntn.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Username */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="username"
                        className="text-sm text-zinc-500"
                      >
                        Username
                      </Label>
                      <div className="relative">
                        <Input
                          id="username"
                          defaultValue="0981"
                          className="pr-8 placeholder:text-zinc-400"
                          placeholder="Enter Username"
                          value={watcher.username}
                          {...register("username", {
                            required: "Required",
                            minLength: {
                              value: 1,
                              message: "Required",
                            },
                            setValueAs: (value) => value.trim(),
                          })}
                        />
                        {errors.username && (
                          <p className="text-red-500 text-sm mt-[5px]">
                            {errors.username.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Org Name */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="orgName"
                        className="text-sm text-zinc-500"
                      >
                        Org Name
                      </Label>
                      <div className="relative">
                        <Input
                          id="orgName"
                          className="pr-8 placeholder:text-zinc-400 "
                          placeholder="Enter Org Name"
                          value={watcher.org_name}
                          {...register("org_name", {
                            required: "Required",
                            minLength: {
                              value: 1,
                              message: "Required",
                            },
                            setValueAs: (value) => value.trim(),
                          })}

                        />
                        {errors.org_name && (
                          <p className="text-red-500 text-sm mt-[5px]">
                            {errors.org_name.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default SRBSettings;
